import React from "react";
import { GraduationCap, Linkedin, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Horizontally-scrolling alumni strip — shown on every institute home page.
 * Renders nothing when `items` is empty.
 *
 * Props:
 *   items     — array of {full_name, current_profession, photo_url, status}
 *   eyebrow   — small label above the heading (e.g., "Alumni Network")
 *   heading   — large heading (e.g., "Where our graduates are today")
 *   theme     — visual variants: "light" (default), "navy", "cream", "school"
 *   className — extra wrapper classes
 *   testid    — testid prefix
 */
const THEMES = {
  light: {
    section: "bg-white py-14",
    container: "max-w-7xl mx-auto px-5",
    eyebrow: "text-[11px] tracking-[0.25em] uppercase text-slate-500 font-semibold",
    heading: "mt-2 font-serif text-3xl md:text-4xl text-slate-900",
    card: "bg-slate-50 border border-slate-200 rounded-sm overflow-hidden",
    placeholder: "bg-slate-100 text-slate-400",
    name: "text-slate-900",
    profession: "text-slate-600",
  },
  navy: {
    section: "bg-[#06183A] text-white py-14",
    container: "max-w-7xl mx-auto px-5",
    eyebrow: "text-[11px] tracking-[0.25em] uppercase text-[#67E8F9] font-semibold",
    heading: "mt-2 font-['Poppins',sans-serif] font-bold text-3xl md:text-4xl",
    card: "bg-[#0B2447] border border-white/10 rounded-2xl overflow-hidden",
    placeholder: "bg-white/5 text-white/40",
    name: "text-white",
    profession: "text-white/70",
  },
  cream: {
    section: "bg-[#F4ECDE] py-14",
    container: "max-w-7xl mx-auto px-5",
    eyebrow: "text-[11px] tracking-[0.25em] uppercase text-[#D48F1A] font-bold",
    heading: "mt-2 font-serif text-3xl md:text-4xl text-[#0B2447]",
    card: "bg-white border border-[#D6C9B4] overflow-hidden",
    placeholder: "bg-[#EFE6D2] text-[#B3A07A]",
    name: "text-[#0B2447]",
    profession: "text-[#5b5650]",
  },
  school: {
    section: "bg-[#FFF8F0] py-14",
    container: "max-w-7xl mx-auto px-5",
    eyebrow: "text-[11px] tracking-[0.25em] uppercase text-[#8B1A1A] font-bold",
    heading: "mt-2 font-bold text-3xl md:text-4xl text-[#1c1a17]",
    card: "bg-white border border-[#E8D9C6] overflow-hidden",
    placeholder: "bg-[#F5E8D5] text-[#B59A78]",
    name: "text-[#1c1a17]",
    profession: "text-[#5b5650]",
  },
};

export default function AlumniStrip({
  items = [],
  eyebrow = "Alumni Network",
  heading = "Where our graduates are today",
  theme = "light",
  className = "",
  testid = "alumni-strip",
  registerHref = "",
}) {
  const t = THEMES[theme] || THEMES.light;
  const active = (items || []).filter((a) => a && (a.status === "Active" || !a.status));
  if (!active.length) return null;

  return (
    <section className={`${t.section} ${className}`} data-testid={testid}>
      <div className={t.container}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className={t.eyebrow}>{eyebrow}</div>
            <h2 className={t.heading}>{heading}</h2>
          </div>
          {registerHref ? (
            <Link
              to={registerHref}
              data-testid={`${testid}-register-cta`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold tracking-wide uppercase border border-current hover:bg-current hover:text-white transition-colors"
            >
              <UserPlus size={14} /> Register as Alumni
            </Link>
          ) : null}
        </div>

        <div
          className="mt-8 grid grid-flow-col auto-cols-[180px] sm:auto-cols-[200px] gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory"
          style={{ scrollbarWidth: "thin" }}
        >
          {active.map((a) => (
            <div
              key={a.id || a.full_name}
              className={`${t.card} snap-start flex flex-col`}
              data-testid={`${testid}-card`}
            >
              <div className="aspect-square overflow-hidden relative">
                {a.photo_url ? (
                  <img src={a.photo_url} alt={a.full_name} className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full flex items-center justify-center ${t.placeholder}`}>
                    <GraduationCap size={36} strokeWidth={1.2} />
                  </div>
                )}
                {a.batch_year && (
                  <div className="absolute top-2 left-2 bg-black/65 text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold backdrop-blur-sm">
                    Batch {a.batch_year}
                  </div>
                )}
                {a.linkedin_url && (
                  <a
                    href={a.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-2 right-2 bg-[#0A66C2] hover:bg-[#084d92] text-white rounded-sm p-1.5 transition-colors shadow-md"
                    aria-label={`${a.full_name} on LinkedIn`}
                    data-testid={`${testid}-linkedin`}
                  >
                    <Linkedin size={12} strokeWidth={2} />
                  </a>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className={`${t.name} font-semibold text-sm leading-snug`}>{a.full_name}</div>
                {a.current_profession && (
                  <div className={`${t.profession} text-xs mt-1 line-clamp-3`}>{a.current_profession}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
